import WorkflowConfigurationAddNodeDrawer from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/WorkflowConfigurationAddNodeDrawer";
import WorkflowConfigurationDrawer from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/WorkflowConfigurationDrawer";
import { Brandmark } from "#src/components/Presentational/Logos/Brandmark";
import { useUpdateWorkflowTemplateByApiVersion } from "#src/components/hooks/adapters/useWorkflowTemplates";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { useBlockNavigation } from "#src/hooks/useBlockNavigation";
import {
  Button,
  ButtonWithPopover,
  Icon,
  LoadingState,
  PageFullScreenContainer,
  useToast,
} from "@validereinc/common-components";
import { AssetTypeType } from "@validereinc/domain";
import {
  Background,
  BackgroundVariant,
  ControlButton,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  type Edge,
  type OnEdgesChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import classNames from "classnames/bind";
import React, { useEffect, useMemo, useState } from "react";

import { WorkflowConfigurationDialog } from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/Components/WorkflowConfigurationDialog";
import ParallelGroupNode from "../ParallelGraphNode/ParallelGraphNode";
import WorkflowGraphEdge from "../WorkflowGraphEdge/WorkflowGraphEdge";
import WorkflowGraphEdgeMarkers from "../WorkflowGraphEdge/WorkflowGraphEdgeMarkers";
import WorkflowGraphNode from "../WorkflowGraphNode/WorkflowGraphNode";
import { MiniMapNode } from "./Components/WorkflowGraphMiniMapNode";
import { useWorkflowGraph } from "./useWorkflowGraph";
import { useWorkflowGraphInteractions } from "./useWorkflowGraphInteractions";
import styles from "./WorkflowGraphCanvas.module.scss";
import { useWorkflowGraphState } from "./WorkflowGraphStore";
const cx = classNames.bind(styles);

export type WorkflowVisualizationProp = {
  mode: "workflow-template";
  templateId: string;
  assetType?: AssetTypeType;
};

// This ID will be set as the outer container of the graph.
// It's also used in the Edge component, to bound its tooltip
// to an element with this ID.
export const WorkflowGraphCanvasOuterBoundaryId = "workflow-graph-container";

export const WorkflowGraphCanvasContent = (
  props: WorkflowVisualizationProp
) => {
  const { toast } = useToast();

  // Permission to edit template:
  const [canEditWorkflowTemplate] = useIsFeatureAvailable({
    featureFlagQuery: "core:workflows",
    permissionQuery: "workflow_templates:write",
  });

  // State management:
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    isInEditMode,
    pendingChangesCount,
    currentConfiguration,
    currentTriggers,
    selectedNode,
    revertAllChanges,
    onSuccessfulSave,
    resetState,
    isSelectingNodeToSourceFormDataFrom,
    isDummyNodeSelected,
    setIsDummyNodeSelected,
    getConfigurationWithVisualizationMetadata,
    markNodeAsDirtyFromMove,
    stepToCopy,
    stepIdToDelete,
    setStepIdToDelete,
    workflowTemplateApiVersion,
  } = useWorkflowGraphState();

  const areThereAnyPendingChanges = pendingChangesCount > 0;

  // !!IMPORTANT: Clear the state of Zustand when unmounting the graph:
  useEffect(() => () => resetState(), []);

  // Helpers:
  const { autoLayoutAndUpdateState, isLoading } = useWorkflowGraph(props);

  useEffect(() => {
    if (currentConfiguration)
      // eslint-disable-next-line no-console
      console.log("configuration", currentConfiguration);
  }, [currentConfiguration]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    if (currentTriggers) console.log("triggers", currentTriggers);
  }, [currentTriggers]);

  // Interactions:
  const {
    fitView,
    zoomIn,
    zoomOut,
    onEdgeMouseEnter,
    onEdgeMouseLeave,
    onNodeMouseEnter,
    onNodeMouseLeave,
    onNodeSelect,
    onNodeDeselect,
    onEnterEditMode,
    onExitEditMode,
    onAddStep,
    onPasteStep,
    onDeleteStep,
    onDeleteTrigger,
  } = useWorkflowGraphInteractions();

  // Confirmation dialog for cancelling all edits
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] =
    useState(false);

  // Confirmation dialog when navigating away from the page
  const [isNavigateAwayConfirmationOpen, setIsNavigateAwayConfirmationOpen] =
    useState(false);

  const isDeleteStepConfirmationOpen = useMemo(() => {
    return !!stepIdToDelete;
  }, [stepIdToDelete]);

  const isDeletingTrigger = stepIdToDelete?.startsWith("trigger-") ?? false;

  // Confirmation dialog when saving changes
  const [
    isSaveTemplateConfigurationDialogOpen,
    setIsSaveTemplateConfigurationDialogOpen,
  ] = useState(false);

  const [, unblockNavigation] = useBlockNavigation(
    areThereAnyPendingChanges,
    () => setIsNavigateAwayConfirmationOpen(true),
    true
  );

  const { mutate, isLoading: isSavingChanges } =
    useUpdateWorkflowTemplateByApiVersion(workflowTemplateApiVersion, {
      templateId: props.templateId,
      noAlerts: true,
      onSuccess: () => {
        toast.push({
          intent: "success",
          description: "Successfully edited the template.",
        });
        onExitEditMode();
        onSuccessfulSave();
      },
    });

  const editButtons = () => {
    if (!canEditWorkflowTemplate) return null;

    const isSaveButtonDisabled = !areThereAnyPendingChanges || !!selectedNode;

    let saveButtonDisabledReason = "";
    if (!areThereAnyPendingChanges) {
      saveButtonDisabledReason = "There are no pending changes to save.";
    }
    if (selectedNode) {
      saveButtonDisabledReason =
        "In order to save, you need to finalize changes on a task or close the sidebar.";
    }

    if (!isInEditMode)
      return (
        <Button
          size="small"
          icon="pencil-simple"
          className={cx("control-button", "edit")}
          onClick={onEnterEditMode}
        >
          Edit
        </Button>
      );

    if (isInEditMode)
      return (
        <>
          <Button
            size="small"
            onClick={() => {
              if (areThereAnyPendingChanges) {
                setIsCancelConfirmationOpen(true);
              } else {
                onExitEditMode();
              }
            }}
          >
            Cancel
          </Button>
          <ButtonWithPopover
            label={saveButtonDisabledReason}
            variant="primary"
            size="small"
            disabled={isSaveButtonDisabled}
            onClick={() => {
              setIsSaveTemplateConfigurationDialogOpen(true);
            }}
          >
            Save
          </ButtonWithPopover>
        </>
      );

    return null;
  };

  const actionButtons = (
    <>
      <div className={cx("top-controls")}>
        {editButtons()}
        <ControlButton
          onClick={() => {
            autoLayoutAndUpdateState({ nodes, edges });
          }}
          className={cx("control-button")}
          title="Auto Layout"
          aria-label="auto layout"
        >
          <Icon
            variant="squares-four"
            size={22}
          />
        </ControlButton>
      </div>
      <div className={cx("side-controls")}>
        <ControlButton
          onClick={() => {
            zoomIn();
          }}
          className={cx("control-button", "zoom-in")}
          title="Zoom In"
          aria-label="zoom in"
        >
          <Icon
            variant="plus-circle"
            size={20}
          />
        </ControlButton>
        <ControlButton
          onClick={() => {
            zoomOut();
          }}
          className={cx("control-button", "zoom-out")}
          title="Zoom Out"
          aria-label="zoom out"
        >
          <Icon
            variant="minus-circle"
            size={20}
          />
        </ControlButton>
        <ControlButton
          onClick={() => {
            fitView();
          }}
          className={cx("control-button")}
          title="Fit Graph to View"
          aria-label="fit to view"
        >
          <Icon
            variant="corners-out"
            size={20}
          />
        </ControlButton>
      </div>
    </>
  );

  if (isLoading || isSavingChanges) {
    let message = "";
    if (isLoading) message = "Loading...";
    if (isSavingChanges) message = "Saving Changes...";

    return (
      <PageFullScreenContainer className={cx("fullscreen-loading")}>
        <LoadingState
          label={message}
          size="normal"
          indicator={<Brandmark isAnimated />}
        />
      </PageFullScreenContainer>
    );
  }

  return (
    <>
      <ReactFlow
        snapToGrid
        snapGrid={[16, 16]}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange as OnEdgesChange<Edge>}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onNodeClick={onNodeSelect}
        onNodeDragStop={(_event, node) => {
          if (isInEditMode) {
            markNodeAsDirtyFromMove(node.id);
          }
        }}
        nodeTypes={{
          workflowNode: WorkflowGraphNode,
          parallelNode: ParallelGroupNode,
        }}
        edgeTypes={{
          workflowEdge: WorkflowGraphEdge,
        }}
        {...(isInEditMode && {
          onEdgeClick: (_, edge) => {
            if (stepToCopy) {
              onPasteStep(edge);
            } else {
              onAddStep(edge);
            }
          },
        })}
        style={{ background: "#f9fafb" }}
        fitView
      >
        <Controls
          position="top-right"
          showInteractive={false}
          showZoom={false}
          showFitView={false}
          className={cx("controls")}
        >
          {actionButtons}
        </Controls>
        <MiniMap
          nodeComponent={MiniMapNode}
          ariaLabel="Workflow Template Mini Map"
          className={cx("minimap")}
        />
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={2}
          color="#c4cbd0"
        />
        {canEditWorkflowTemplate && (
          <Panel position={selectedNode ? "top-left" : "top-center"}>
            <div
              className={cx("edit-mode-banner")}
              style={{
                opacity: isInEditMode ? 1 : 0,
              }}
            >
              {`You are in ${
                isSelectingNodeToSourceFormDataFrom ? "selection" : "edit"
              } mode. Click ”Save” to confirm edits.`}
            </div>
          </Panel>
        )}
      </ReactFlow>
      <WorkflowConfigurationDrawer
        onClose={() => {
          onNodeDeselect();
        }}
        selectedNode={
          isDummyNodeSelected
            ? undefined
            : selectedNode?.type !== "parallelNode"
              ? selectedNode
              : undefined
        }
        workflowTemplate={{
          config: currentConfiguration,
          triggers: currentTriggers,
          assetType: props.assetType,
        }}
      />
      <WorkflowConfigurationAddNodeDrawer
        isOpen={isDummyNodeSelected}
        onClose={() => {
          onNodeDeselect();
          setIsDummyNodeSelected(false);
        }}
        selectedNode={
          selectedNode?.type !== "parallelNode" ? selectedNode : undefined
        }
      />

      <WorkflowConfigurationDialog
        isOpen={isCancelConfirmationOpen}
        onClose={() => {
          setIsCancelConfirmationOpen(false);
        }}
        onConfirm={() => {
          revertAllChanges();
          onExitEditMode();
        }}
        buttonLabel="Cancel Template Edits"
        buttonVariant="error"
        title="Cancel Template Edits"
        message="Canceling your template edits revert the template to its configuration prior to editing
            and delete all edits from this session. This action cannot be undone."
      />
      <WorkflowConfigurationDialog
        isOpen={isNavigateAwayConfirmationOpen}
        onClose={() => {
          setIsNavigateAwayConfirmationOpen(false);
        }}
        onConfirm={() => {
          unblockNavigation({
            redirect: true,
          });
        }}
        buttonLabel="Leave Without Saving"
        buttonVariant="error"
        title="Leave Without Saving?"
        message="Leaving an in-progress template will delete all of your changes. This action cannot be undone."
      />
      <WorkflowConfigurationDialog
        isOpen={isDeleteStepConfirmationOpen}
        onClose={() => {
          setStepIdToDelete(undefined);
        }}
        onConfirm={() => {
          const wasDeleted = isDeletingTrigger
            ? onDeleteTrigger(stepIdToDelete)
            : onDeleteStep(stepIdToDelete);

          if (!wasDeleted) {
            toast.push({
              intent: "warning",
              description: isDeletingTrigger
                ? "This trigger could not be deleted."
                : "This step cannot be fully deleted. Review any references to this step or its children that are outside of the branch.",
            });
          }
        }}
        buttonLabel="Delete"
        buttonVariant="error"
        title={isDeletingTrigger ? "Delete Trigger?" : "Delete Step?"}
        message={
          isDeletingTrigger
            ? "Deleting this trigger removes it from the workflow template. This action cannot be undone."
            : "Deleting the step will also delete any edges coming in or out of the step. This action cannot be undone."
        }
      />
      <WorkflowConfigurationDialog
        isOpen={isSaveTemplateConfigurationDialogOpen}
        onClose={() => {
          setIsSaveTemplateConfigurationDialogOpen(false);
        }}
        onConfirm={() => {
          if (!currentConfiguration) return;

          const { configuration, triggers } =
            getConfigurationWithVisualizationMetadata();

          onNodeDeselect();
          mutate({
            id: props.templateId,
            data: {
              config: configuration,
              ...(triggers ? { triggers } : {}),
            },
          });
          setIsSaveTemplateConfigurationDialogOpen(false);
        }}
        buttonLabel="Save"
        title="Save Template Edits"
        message="Saving your edits will generate a new version of the template. All
      in-progress workflows will retain the existing template configuration, but
      new workflows will inherit the edited template configuration."
      />
    </>
  );
};

export const WorkflowGraphCanvas = (props: WorkflowVisualizationProp) => (
  <ReactFlowProvider>
    <WorkflowGraphCanvasContent {...props} />
    <WorkflowGraphEdgeMarkers />
  </ReactFlowProvider>
);
